import { useVault, useUpdateVault, vaultKeys } from '@/hooks/useVault';
import { useVaultKeyStore } from '@/store/vaultKeyStore';
import { DocumentType } from '@/types/vault.types';
import {
  unlockVault as unlockVaultCrypto,
  migrateVaultKeys,
} from '@/utils/crypto/vaultCrypto';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

const unlockSchema = z.object({
  password: z.string().min(1, 'vault.validation.password.required'),
});

const uploadSchema = z.object({
  documentType: z.nativeEnum(DocumentType),
  month: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const parsed = Number(val);
      return isNaN(parsed) ? undefined : parsed;
    },
    z.number().min(1).max(12).optional(),
  ),
  year: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const parsed = Number(val);
      return isNaN(parsed) ? undefined : parsed;
    },
    z.number().min(2000).max(2100).optional(),
  ),
});

type UnlockFormData = z.infer<typeof unlockSchema>;
type UploadFormData = z.infer<typeof uploadSchema>;

interface UnlockVaultModalProps {
  vaultId: string;
  isOpen: boolean;
  onClose: () => void;
  unlockOnly?: boolean;
}

export const UnlockVaultModal = ({
  vaultId,
  isOpen,
  onClose,
}: UnlockVaultModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { data: vaultData } = useVault(vaultId);
  // const uploadDocument = useUploadDocument(vaultId);
  const unlockVault = useVaultKeyStore((state) => state.unlockVault);
  const isVaultUnlocked = useVaultKeyStore((state) => state.isVaultUnlocked);
  // const getMasterKey = useVaultKeyStore((state) => state.getMasterKey);

  const {
    register: registerUnlock,
    handleSubmit: handleUnlockSubmit,
    formState: { errors: unlockErrors, isSubmitting: isUnlocking },
    reset: resetUnlock,
  } = useForm<UnlockFormData>({
    resolver: zodResolver(unlockSchema),
  });

  const {
    // register: registerUpload,
    // handleSubmit: handleUploadSubmit,
    formState: { isSubmitting: isUploading, errors: uploadErrors },
    reset: resetUpload,
    // watch,
  } = useForm<UploadFormData>({
    resolver: zodResolver(uploadSchema),
    defaultValues: {
      documentType: DocumentType.OTHER,
    },
  });

  // Debug: Log form errors
  console.log('[UnlockVaultModal] Upload form errors:', uploadErrors);
  console.log('[UnlockVaultModal] Selected file:', selectedFile);
  console.log('[UnlockVaultModal] Is uploading:', isUploading);

  // const documentType = watch('documentType');

  const updateVault = useUpdateVault(vaultId);

  const onUnlockSubmit = async (data: UnlockFormData) => {
    try {
      // 1. Get vault salt and encrypted vault key from backend
      if (!vaultData?.vaultKeySalt || !vaultData?.encryptedVaultKey) {
        throw new Error('Vault not initialized or missing encryption data');
      }

      // 2. Derive master key (DEK) from password + salt + encrypted vault key (client-side)
      // This decrypts encryptedVaultKey with KEK to get the actual vault master key
      const masterKey = await unlockVaultCrypto(
        data.password,
        vaultData.vaultKeySalt,
        vaultData.encryptedVaultKey
      );

      // 3. Store master key in memory (auto-lock after 30 minutes)
      unlockVault(vaultId, masterKey);

      // 4. Check & Migrate Keys (Zero-Knowledge)
      if (!vaultData.publicKey || !vaultData.encryptedPrivateKey) {
        console.log('[UnlockVaultModal] Migrating vault keys (RSA generation)...');
        try {
          const { publicKey, encryptedPrivateKey } = await migrateVaultKeys(masterKey);
          await updateVault.mutateAsync({
            publicKey,
            encryptedPrivateKey,
          });
          console.log('[UnlockVaultModal] Keys migrated successfully');

          // Wait for vault data to be refetched before continuing
          await queryClient.refetchQueries({ queryKey: vaultKeys.detail(vaultId) });
          console.log('[UnlockVaultModal] Vault data refetched after migration');

          toast.success(t('vault.migration.success', 'Clés de sécurité mises à jour'));
        } catch (migrationError) {
          console.error('[UnlockVaultModal] Key migration failed:', migrationError);
          toast.error(t('vault.migration.error', 'Erreur lors de la migration des clés'));
        }
      }

      toast.success(t('vault.unlock.success', 'Coffre déverrouillé!'));
      console.log('[UnlockVaultModal] Vault unlocked successfully');

      // If unlockOnly mode, close the modal after successful unlock
      handleClose();
    } catch (error) {
      console.error('[UnlockVaultModal] Failed to unlock vault:', error);
      toast.error(t('vault.unlock.error', 'Mot de passe incorrect'));
      throw error;
    }
  };

  // const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = event.target.files?.[0];
  //   if (file) {
  //     setSelectedFile(file);
  //   }
  // };

  // const onUploadSubmit = async (data: UploadFormData) => {
  //   console.log('[UnlockVaultModal] onUploadSubmit called', { data, selectedFile });
  //   if (!selectedFile) {
  //     console.log('[UnlockVaultModal] No file selected');
  //     toast.error(t('vault.upload.noFile', 'Veuillez sélectionner un fichier'));
  //     return;
  //   }

  //   try {
  //     // 1. Get vault master key from memory
  //     const masterKey = getMasterKey(vaultId);
  //     console.log('[UnlockVaultModal] Vault key retrieved:', !!masterKey);
  //     if (!masterKey) {
  //       throw new Error('Vault is locked. Please unlock it first.');
  //     }

  //     // 2. Read file as ArrayBuffer
  //     const fileBuffer = await selectedFile.arrayBuffer();

  //     // 3. Encrypt file client-side (document + document key)
  //     const encrypted = await encryptDocumentComplete(fileBuffer, masterKey);

  //     // 4. Convert encrypted content to Blob
  //     const encryptedBlob = new Blob([encrypted.encryptedContent], {
  //       type: 'application/octet-stream',
  //     });

  //     // 5. Prepare upload data with encrypted file + metadata
  //     const uploadData: UploadDocumentDto = {
  //       file: encryptedBlob,
  //       metadata: {
  //         originalName: selectedFile.name,
  //         originalSize: selectedFile.size,
  //         mimeType: selectedFile.type,
  //         iv: encrypted.iv,
  //         authTag: encrypted.authTag,
  //         encryptedDocumentKey: encrypted.encryptedDocumentKey,
  //         documentKeyIv: encrypted.documentKeyIv,
  //         documentKeyAuthTag: encrypted.documentKeyAuthTag,
  //         encryptionAlgorithm: 'AES-256-GCM',
  //       },
  //       type: data.documentType,
  //     };

  //     // Add period if month and year provided
  //     if (data.month && data.year) {
  //       uploadData.period = {
  //         month: data.month,
  //         year: data.year,
  //       };
  //     }

  //     // 6. Upload encrypted file to server
  //     await uploadDocument.mutateAsync(uploadData);

  //     console.log('[UnlockVaultModal] Document uploaded successfully');
  //     toast.success(t('vault.upload.success', 'Document chiffré et envoyé!'));
  //     handleClose();
  //   } catch (error) {
  //     console.error('[UnlockVaultModal] Failed to upload document:', error);
  //     toast.error(t('vault.upload.error', 'Échec de l\'upload'));
  //     throw error;
  //   }
  // };

  const handleClose = () => {
    resetUnlock();
    resetUpload();
    setSelectedFile(null);
    onClose();
  };

  const isUnlocked = isVaultUnlocked(vaultId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
        >
          <X className="h-5 w-5" />
        </button>

        {!isUnlocked ? (
          <>
            {/* Unlock Form */}
            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                <Lock className="h-8 w-8 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900">
                {t('vault.unlock.title', 'Déverrouiller le Coffre')}
              </h2>
              <p className="mt-2 text-sm text-neutral-600">
                {t(
                  'vault.unlock.description',
                  'Entrez votre mot de passe pour accéder au coffre',
                )}
              </p>
            </div>

            <form onSubmit={handleUnlockSubmit(onUnlockSubmit)} className="space-y-4">
              <div>
                <label
                  htmlFor="password"
                  className="mb-2 block text-sm font-medium text-neutral-700"
                >
                  {t('vault.unlock.password', 'Mot de passe')}
                  <span className="text-error-600">*</span>
                </label>
                <input
                  {...registerUnlock('password')}
                  type="password"
                  id="password"
                  className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:outline-none"
                  placeholder="••••••••••••"
                />
                {unlockErrors.password && (
                  <p className="mt-1 text-sm text-error-600">
                    {t(unlockErrors.password.message as string)}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                >
                  {t('common.cancel', 'Annuler')}
                </button>
                <button
                  type="submit"
                  disabled={isUnlocking}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                >
                  <Lock className="h-4 w-4" />
                  {isUnlocking
                    ? t('common.unlocking', 'Déverrouillage...')
                    : t('vault.unlock.submit', 'Déverrouiller')}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div />
          // <>
          //   {/* Upload Form */}
          //   <div className="mb-6 text-center">
          //     <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-100">
          //       <Upload className="h-8 w-8 text-success-600" />
          //     </div>
          //     <h2 className="text-2xl font-bold text-neutral-900">
          //       {t('vault.upload.title', 'Téléverser un Document')}
          //     </h2>
          //     <p className="mt-2 text-sm text-neutral-600">
          //       {t(
          //         'vault.upload.description',
          //         'Le document sera chiffré avant envoi',
          //       )}
          //     </p>
          //   </div>

          //   <form onSubmit={handleUploadSubmit(onUploadSubmit)} className="space-y-4">
          //     {/* File Input */}
          //     <div>
          //       <label className="mb-2 block text-sm font-medium text-neutral-700">
          //         {t('vault.upload.file', 'Fichier')}
          //         <span className="text-error-600">*</span>
          //       </label>
          //       <input
          //         type="file"
          //         onChange={handleFileSelect}
          //         className="block w-full text-sm text-neutral-900 file:mr-4 file:rounded-lg file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
          //         required
          //       />
          //       {selectedFile && (
          //         <p className="mt-1 text-sm text-neutral-600">
          //           {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
          //         </p>
          //       )}
          //     </div>

          //     {/* Document Type */}
          //     <div>
          //       <label className="mb-2 block text-sm font-medium text-neutral-700">
          //         {t('vault.upload.type', 'Type de document')}
          //         <span className="text-error-600">*</span>
          //       </label>
          //       <select
          //         {...registerUpload('documentType')}
          //         className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:outline-none"
          //       >
          //         <option value={DocumentType.PAYSLIP}>
          //           {t('vault.documentType.payslip', 'Bulletin de paie')}
          //         </option>
          //         <option value={DocumentType.DSN}>
          //           {t('vault.documentType.dsn', 'DSN')}
          //         </option>
          //         <option value={DocumentType.EDITION}>
          //           {t('vault.documentType.edition', 'Édition')}
          //         </option>
          //         <option value={DocumentType.CONTRACT}>
          //           {t('vault.documentType.contract', 'Contrat')}
          //         </option>
          //         <option value={DocumentType.OTHER}>
          //           {t('vault.documentType.other', 'Autre')}
          //         </option>
          //       </select>
          //     </div>

          //     {/* Period (optional) */}
          //     {(documentType === DocumentType.PAYSLIP || documentType === DocumentType.EDITION) && (
          //       <div className="grid grid-cols-2 gap-4">
          //         <div>
          //           <label className="mb-2 block text-sm font-medium text-neutral-700">
          //             {t('vault.upload.month', 'Mois')}
          //           </label>
          //           <input
          //             {...registerUpload('month', { valueAsNumber: true })}
          //             type="number"
          //             min="1"
          //             max="12"
          //             className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:outline-none"
          //             placeholder="1-12"
          //           />
          //         </div>
          //         <div>
          //           <label className="mb-2 block text-sm font-medium text-neutral-700">
          //             {t('vault.upload.year', 'Année')}
          //           </label>
          //           <input
          //             {...registerUpload('year', { valueAsNumber: true })}
          //             type="number"
          //             min="2000"
          //             max="2100"
          //             className="block w-full rounded-lg border border-neutral-300 px-3 py-2 shadow-sm transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:outline-none"
          //             placeholder="2024"
          //           />
          //         </div>
          //       </div>
          //     )}

          //     <div className="flex gap-3 pt-2">
          //       <button
          //         type="button"
          //         onClick={handleClose}
          //         className="flex-1 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          //       >
          //         {t('common.cancel', 'Annuler')}
          //       </button>
          //       <button
          //         type="submit"
          //         disabled={isUploading || !selectedFile}
          //         className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          //       >
          //         <Upload className="h-4 w-4" />
          //         {isUploading
          //           ? t('common.uploading', 'Téléversement...')
          //           : t('vault.upload.submit', 'Téléverser')}
          //       </button>
          //     </div>
          //   </form>
          // </>
        )}
      </div>
    </div>
  );
};
